import React, { useEffect, useRef, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { IBlogCategory, IBlogPost, IAuthorProfile } from '../../core/interfaces'
import {
    createBlogPost,
    getBlogPost,
    updateBlogPost,
    deleteBlogPost,
} from '../../services/blog-posts'
import { getAllBlogCategoriesFlat } from '../../services/blog-categories'
import { getAllAuthorProfiles } from '../../services/blog-author-profiles'
import { uploadImage } from '../../services/upload-image'
import { MessageBox } from '../../core/components/MessageBox'
import { DeleteConfirmationModal } from '../../core/components/DeleteConfirmationModal'
import { UUID } from 'node:crypto'

interface Props {
    show: boolean
    onHide: () => void
    editPostId?: UUID
    onSaved: () => void
}

const toSlug = (input: string): string =>
    input
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '-')
        .replace(/[^\w-]/g, '')
        .toLowerCase()

const EMPTY_FORM: IBlogPost = {
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    coverImageUrl: '',
    published: false,
    isPrivate: false,
    categoryIds: [],
    authorId: undefined,
    metaTitle: '',
    metaDescription: '',
}

export const BlogPostModal: React.FC<Props> = ({ show, onHide, editPostId, onSaved }) => {
    const [form, setForm] = useState<IBlogPost>(EMPTY_FORM)
    const [categories, setCategories] = useState<IBlogCategory[]>([])
    const [authors, setAuthors] = useState<IAuthorProfile[]>([])
    const [isUploading, setIsUploading] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isError, setIsError] = useState(false)
    const [showMessageBox, setShowMessageBox] = useState(false)
    const [message, setMessage] = useState('')
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const slugManuallyEdited = useRef(false)

    const isEdit = !!editPostId

    useEffect(() => {
        if (!show) return
        slugManuallyEdited.current = false

        getAllBlogCategoriesFlat()
            .then((res) => setCategories(res.data))
            .catch(console.error)

        getAllAuthorProfiles({ page: 0, size: 100 })
            .then((res) => setAuthors(res.data.content))
            .catch(console.error)

        if (editPostId) {
            setIsLoading(true)
            getBlogPost(editPostId)
                .then((res) => {
                    setForm({
                        ...res.data,
                        categoryIds: res.data.categoryIds ?? [],
                    })
                    slugManuallyEdited.current = true
                })
                .catch((e) => {
                    setIsError(true)
                    setMessage(e.message)
                    setShowMessageBox(true)
                })
                .finally(() => setIsLoading(false))
        } else {
            setForm(EMPTY_FORM)
        }
    }, [show, editPostId])

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const title = e.target.value
        setForm((prev) => ({
            ...prev,
            title,
            slug: slugManuallyEdited.current ? prev.slug : toSlug(title),
        }))
    }

    const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        slugManuallyEdited.current = true
        setForm((prev) => ({ ...prev, slug: e.target.value }))
    }

    const handleField = (field: keyof IBlogPost) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
    ) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }))
    }

    const handleCheck = (field: 'published' | 'isPrivate') => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({ ...prev, [field]: e.target.checked }))
    }

    const handleCategoryToggle = (id: UUID) => {
        setForm((prev) => {
            const ids = prev.categoryIds ?? []
            return {
                ...prev,
                categoryIds: ids.includes(id) ? ids.filter((c) => c !== id) : [...ids, id],
            }
        })
    }

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        const result = await uploadImage(
            file,
            'blog',
            setIsUploading,
            setIsError,
            setShowMessageBox,
            setMessage,
        )
        if (result.photoUrl) {
            setForm((prev) => ({ ...prev, coverImageUrl: result.photoUrl }))
        }
    }

    const handleSave = async () => {
        if (!form.title?.trim()) {
            setIsError(true)
            setMessage('Title is required.')
            setShowMessageBox(true)
            return
        }
        if (!form.coverImageUrl?.trim()) {
            setIsError(true)
            setMessage('Cover image is required.')
            setShowMessageBox(true)
            return
        }
        if (!form.content?.trim()) {
            setIsError(true)
            setMessage('Content is required.')
            setShowMessageBox(true)
            return
        }

        try {
            setIsLoading(true)
            if (isEdit && editPostId) {
                await updateBlogPost(editPostId, form)
            } else {
                await createBlogPost(form)
            }
            setIsError(false)
            setMessage(isEdit ? 'Post updated successfully.' : 'Post created successfully.')
            setShowMessageBox(true)
            onSaved()
            handleClose()
        } catch (e: any) {
            setIsError(true)
            setMessage(e?.message ?? 'An error occurred.')
            setShowMessageBox(true)
        } finally {
            setIsLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!editPostId) return
        try {
            await deleteBlogPost(editPostId)
            onSaved()
            handleClose()
        } catch (e: any) {
            setIsError(true)
            setMessage(e?.message ?? 'Delete failed.')
            setShowMessageBox(true)
        }
        setShowDeleteConfirm(false)
    }

    const handleClose = () => {
        setForm(EMPTY_FORM)
        slugManuallyEdited.current = false
        onHide()
    }

    return (
        <>
            <Modal show={show} onHide={handleClose} size="xl" backdrop="static" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{isEdit ? 'Edit Blog Post' : 'Create Blog Post'}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {isLoading && <p className="text-muted">Loading...</p>}

                    <Form className="d-flex flex-column gap-3">
                        {/* Title */}
                        <Form.Group>
                            <Form.Label className="fw-bold">Title *</Form.Label>
                            <Form.Control
                                type="text"
                                value={form.title}
                                onChange={handleTitleChange}
                                placeholder="Post title"
                            />
                        </Form.Group>

                        {/* Slug */}
                        <Form.Group>
                            <Form.Label className="fw-bold">Slug</Form.Label>
                            <Form.Control
                                type="text"
                                value={form.slug}
                                onChange={handleSlugChange}
                                placeholder="auto-generated-from-title"
                            />
                            <Form.Text className="text-muted">
                                Auto-generated from title. Edit only if needed.
                            </Form.Text>
                        </Form.Group>

                        {/* Excerpt */}
                        <Form.Group>
                            <Form.Label className="fw-bold">Excerpt</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                maxLength={500}
                                value={form.excerpt ?? ''}
                                onChange={handleField('excerpt')}
                                placeholder="Short description (max 500 chars)"
                            />
                        </Form.Group>

                        {/* Cover Image */}
                        <Form.Group>
                            <Form.Label className="fw-bold">Cover Image *</Form.Label>
                            <div className="d-flex align-items-center gap-3">
                                <Form.Control
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    disabled={isUploading}
                                    style={{ maxWidth: 320 }}
                                />
                                {isUploading && <span className="text-muted">Uploading...</span>}
                            </div>
                            {form.coverImageUrl && (
                                <div className="mt-2">
                                    <img
                                        src={form.coverImageUrl}
                                        alt="Cover"
                                        style={{ maxHeight: 140, maxWidth: 260, objectFit: 'cover', borderRadius: 6 }}
                                    />
                                    <div>
                                        <Form.Text className="text-muted">{form.coverImageUrl}</Form.Text>
                                    </div>
                                </div>
                            )}
                        </Form.Group>

                        {/* Content (raw HTML) */}
                        <Form.Group>
                            <Form.Label className="fw-bold">Content (HTML) *</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={16}
                                value={form.content ?? ''}
                                onChange={handleField('content')}
                                placeholder="Paste raw HTML content here..."
                                style={{ fontFamily: 'monospace', fontSize: 13 }}
                            />
                        </Form.Group>

                        {/* Author */}
                        <Form.Group>
                            <Form.Label className="fw-bold">Author</Form.Label>
                            <Form.Select
                                value={form.authorId ?? ''}
                                onChange={(e) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        authorId: e.target.value ? (e.target.value as UUID) : undefined,
                                    }))
                                }
                            >
                                <option value="">— Echipa RaiseAReader (default) —</option>
                                {authors.map((a) => (
                                    <option key={a.id as string} value={a.id as string}>
                                        {a.authorName}
                                        {a.subheading ? ` · ${a.subheading}` : ''}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        {/* Categories */}
                        <Form.Group>
                            <Form.Label className="fw-bold">Categories</Form.Label>
                            <div className="d-flex flex-wrap gap-2">
                                {categories.map((cat) => (
                                    <Form.Check
                                        key={cat.id as string}
                                        type="checkbox"
                                        id={`cat-${cat.id}`}
                                        label={cat.name}
                                        checked={(form.categoryIds ?? []).includes(cat.id as UUID)}
                                        onChange={() => handleCategoryToggle(cat.id as UUID)}
                                    />
                                ))}
                                {categories.length === 0 && (
                                    <span className="text-muted">No categories yet. Add some from Blog Categories.</span>
                                )}
                            </div>
                        </Form.Group>

                        {/* SEO */}
                        <Form.Group>
                            <Form.Label className="fw-bold">Meta Title</Form.Label>
                            <Form.Control
                                type="text"
                                maxLength={255}
                                value={form.metaTitle ?? ''}
                                onChange={handleField('metaTitle')}
                                placeholder="Defaults to post title if left blank"
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label className="fw-bold">Meta Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                maxLength={500}
                                value={form.metaDescription ?? ''}
                                onChange={handleField('metaDescription')}
                                placeholder="Defaults to excerpt if left blank (max 500 chars)"
                            />
                        </Form.Group>

                        {/* Flags */}
                        <div className="d-flex gap-4">
                            <Form.Check
                                type="switch"
                                id="published-switch"
                                label="Published"
                                checked={form.published}
                                onChange={handleCheck('published')}
                            />
                            <Form.Check
                                type="switch"
                                id="private-switch"
                                label="Private (subscription only)"
                                checked={form.isPrivate}
                                onChange={handleCheck('isPrivate')}
                            />
                        </div>
                    </Form>
                </Modal.Body>

                <Modal.Footer>
                    {isEdit && (
                        <Button
                            className="btn btn-danger me-auto"
                            onClick={() => setShowDeleteConfirm(true)}
                        >
                            Delete
                        </Button>
                    )}
                    <Button className="btn btn-secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={isLoading || isUploading}
                    >
                        {isLoading ? 'Saving...' : 'Save'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <DeleteConfirmationModal
                showDeleteConfirmationModal={showDeleteConfirm}
                setDeleteConfirmationModal={setShowDeleteConfirm}
                deleteTitle="Delete Blog Post"
                deleteFunc={handleDelete}
            />

            <MessageBox
                message={message}
                setShowMessageBox={setShowMessageBox}
                showMessageBox={showMessageBox}
                isError={isError}
            />
        </>
    )
}
